import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Check, ShieldCheck, Truck, Wrench, ArrowLeft, ShoppingBag, Zap, ZoomIn } from 'lucide-react';
import { PRODUCTS, COLLECTIONS, COMPANY_INFO } from '../mock';
import { toast } from '../hooks/use-toast';
import { useCart } from '../context/CartContext';
import { buildCheckoutUrl } from '../lib/checkout';
import { getBrandForName } from '../data/brands';
import ImageLightbox from '../components/ImageLightbox';
import { useStockStatus } from '../hooks/useStockStatus';

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === '1';
  const [fetchedProduct, setFetchedProduct] = useState(null);
  const storeProduct = useMemo(() => PRODUCTS.find((p) => p.slug === slug), [slug]);
  const product = storeProduct || fetchedProduct;
  React.useEffect(() => {
    if (storeProduct || !isPreview) return;
    const token = localStorage.getItem('fad_admin_token');
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/catalog/preview/${slug}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && d.product) setFetchedProduct(d.product); })
      .catch(() => {});
  }, [slug, isPreview, storeProduct]);
  const [activeImg, setActiveImg] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const hasVariants = !!(product && product.variants && product.variants.length > 1);
  // A single locked variant (e.g. Fortune FRP body kits) should still display
  // its chip — pre-selected, non-clickable — so customers see the material.
  const lockedSingleVariant = !!(
    product &&
    product.variants &&
    product.variants.length === 1 &&
    product.variants[0]?.locked
  );
  const showVariantPicker = hasVariants || lockedSingleVariant;
  const [variantIdx, setVariantIdx] = useState(0);
  // Track whether the user has *actively* picked a variant/size. Used to gate BUY NOW
  // so customers can't check out a variable product without choosing options first.
  const [selectionMade, setSelectionMade] = useState(false);
  // Diameters may be strings (legacy) or {size, price} objects. Normalise.
  const activeDiameters = useMemo(() => {
    if (!product) return [];
    const v = product.variants && product.variants[variantIdx];
    const raw = (v && v.diameters) || product.diameters || [];
    return raw.map((d) => (typeof d === 'string' ? { size: d, price: null } : d));
  }, [product, variantIdx]);
  const hasDiameters = activeDiameters.length > 0;
  const [diameter, setDiameter] = useState((activeDiameters[0] && activeDiameters[0].size) || '');
  // Variant-specific image gallery (falls back to product images if variant has none).
  const activeImages = useMemo(() => {
    if (!product) return [];
    const v = product.variants && product.variants[variantIdx];
    if (v && Array.isArray(v.images) && v.images.length > 0) return v.images;
    return product.images || [];
  }, [product, variantIdx]);
  React.useEffect(() => {
    // Reset to first image whenever the variant (and therefore gallery) changes.
    setActiveImg(0);
  }, [variantIdx]);
  React.useEffect(() => {
    if (!activeDiameters.length) {
      setDiameter('');
      return;
    }
    if (!activeDiameters.some((d) => d.size === diameter)) {
      setDiameter(activeDiameters[0].size);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantIdx]);
  const selectedDiameter = useMemo(
    () => activeDiameters.find((d) => d.size === diameter) || null,
    [activeDiameters, diameter]
  );
  const [form, setForm] = useState({ name: '', email: '', notes: '' });
  const { addItem } = useCart();
  const stockStatus = useStockStatus(slug);
  const isInStock = stockStatus === 'instock';
  const isLoadingStock = stockStatus === null;

  if (!product) {
    return (
      <div className="bg-white min-h-screen pt-40 pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-neutral-900">
          {isPreview ? (
            <h1 className="font-display text-2xl">Loading preview…</h1>
          ) : (
            <>
              <h1 className="font-display text-4xl">Product not found</h1>
              <Link to="/shop" className="text-[#FF6B00] underline mt-6 inline-block">Back to Shop</Link>
            </>
          )}
        </div>
      </div>
    );
  }

  const collection = COLLECTIONS.find((c) => c.id === product.collectionId);
  const related = PRODUCTS.filter((p) => p.collectionId === product.collectionId && p.id !== product.id).slice(0, 3);
  const activeVariant = hasVariants ? product.variants[variantIdx] : (product.variants && product.variants[0]);
  // Price priority: selected diameter > active variant > product base price.
  const displayPrice =
    (selectedDiameter && selectedDiameter.price != null && selectedDiameter.price) ||
    (activeVariant && activeVariant.price) ||
    product.price;
  const displayCompare = activeVariant && activeVariant.compareAtPrice ? activeVariant.compareAtPrice : product.compareAtPrice;
  const onSale = displayCompare && displayCompare > displayPrice;

  const requestQuote = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: 'Missing fields', description: 'Name and email are required.' });
      return;
    }
    const recipients = (COMPANY_INFO.inquiryRecipients || [COMPANY_INFO.email]).join(',');
    const subject = `Quote Request: ${product.name}`;
    const productUrl = typeof window !== 'undefined' ? window.location.href : '';
    const variantLine = activeVariant && activeVariant.option ? `Option: ${activeVariant.option}` : null;
    const skuLine = activeVariant && activeVariant.sku ? `SKU: ${activeVariant.sku}` : null;
    const bodyLines = [
      `Product: ${product.name}`,
      `Brand: ${product.brand}`,
      variantLine,
      skuLine,
      `Listed Price: $${displayPrice.toLocaleString()} USD`,
      productUrl ? `Link: ${productUrl}` : null,
      '',
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      form.notes ? `\nNotes:\n${form.notes}` : '',
      '',
      '\u2014',
      'Sent from fortuneautodesign.com product page',
    ].filter((l) => l !== null);
    const body = bodyLines.join('\n');
    const href = `mailto:${recipients}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = href;
    const list = JSON.parse(localStorage.getItem('fad_product_quotes') || '[]');
    list.push({ productId: product.id, productName: product.name, sku: activeVariant && activeVariant.sku, option: activeVariant && activeVariant.option, ...form, at: new Date().toISOString() });
    localStorage.setItem('fad_product_quotes', JSON.stringify(list));
    toast({ title: 'Opening your email app\u2026', description: `Please confirm and send to complete your quote request for ${product.name}.` });
    setForm({ name: '', email: '', notes: '' });
  };

  // Whether the customer still needs to pick something before they can buy.
  // True for any product that exposes a multi-variant picker or wheel diameter
  // selector — false for simple single-variant items so the CTA is enabled.
  const needsSelection = (hasVariants || hasDiameters) && !selectionMade;

  const addAndReview = ({ goToCheckout = false } = {}) => {
    if (needsSelection) return;
    const lineId = `${product.id}|${activeVariant?.sku || ''}|${activeVariant?.option || ''}|${diameter}`;
    addItem({
      lineId,
      productId: product.id,
      slug: product.slug,
      name: product.name,
      price: displayPrice,
      image: (activeImages[0] || product.images?.[0]),
      variantSku: activeVariant?.sku || '',
      variantOption: [activeVariant?.option, diameter].filter(Boolean).join(' \u00b7 '),
      quantity: 1,
      specialOrder: true,
    }, { open: !goToCheckout });   // skip drawer when jumping straight to checkout
    if (goToCheckout) {
      navigate('/checkout');
    } else {
      toast({ title: 'Added to cart', description: `${product.name} \u2014 $${displayPrice.toLocaleString()} USD` });
    }
  };

  /**
   * Two distinct CTAs per the testing-agent review:
   *  - RESERVE MY BUILD (primary, orange): adds item + jumps directly to /checkout
   *  - ADD TO CART (outline secondary): adds item + opens cart drawer for more browsing
   */
  const renderProductCTAs = (variant) => (
    <div
      className={variant === 'mobile' ? 'md:hidden' : 'hidden md:block'}
      data-testid={`product-cta-block-${variant}`}
    >
      <button
        type="button"
        disabled={needsSelection}
        onClick={() => addAndReview({ goToCheckout: true })}
        data-testid={`product-buy-now-button-${variant}`}
        aria-disabled={needsSelection}
        title={needsSelection ? 'Please select an option before continuing' : 'Buy now'}
        className={`w-full mb-3 px-6 py-4 font-display tracking-[0.22em] text-sm font-bold transition-colors inline-flex items-center justify-center gap-2 border-2 ${
          needsSelection
            ? 'bg-neutral-200 border-neutral-200 text-neutral-500 cursor-not-allowed'
            : 'bg-[#FF6B00] hover:bg-[#E25500] border-[#FF6B00] text-white'
        }`}
      >
        <Zap size={16} /> {needsSelection ? 'SELECT AN OPTION' : 'BUY NOW'}
      </button>

      <button
        type="button"
        disabled={needsSelection}
        onClick={addAndReview}
        data-testid={`product-add-to-cart-${variant}`}
        aria-disabled={needsSelection}
        className={`w-full mb-3 px-6 py-3 font-display tracking-[0.22em] text-xs font-bold transition-colors inline-flex items-center justify-center gap-2 border-2 ${
          needsSelection
            ? 'border-neutral-200 text-neutral-400 cursor-not-allowed'
            : 'border-neutral-300 hover:border-neutral-900 text-neutral-900 hover:bg-neutral-50'
        }`}
      >
        ADD TO CART
      </button>

      {/* Soft informational disclaimer: submit-request / pay-later flow + lead time. */}
      <div
        className="bg-neutral-50 border border-neutral-200 px-4 py-3 mb-2 rounded-sm"
        data-testid={`product-shipping-timeline-disclaimer-${variant}`}
      >
        <p className="text-[11px] md:text-[12px] text-neutral-600 leading-relaxed">
          <strong className="text-neutral-900">No payment now.</strong> Submit your order request and our team reviews availability, then emails you a secure invoice to pay by card. Prices are shown in USD. Production begins once payment is confirmed, and shipping is billed separately when your build is ready. Lead times vary by product &mdash; please <a href="/contact" className="underline">contact us</a> for a specific timeline.
        </p>
      </div>
    </div>
  );

  return (
    <div className="bg-white text-neutral-900">
      {isPreview && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 text-black text-center text-xs font-display tracking-[0.15em] py-1.5" data-testid="preview-banner">
          PREVIEW MODE {product.published === false ? '· DRAFT (not visible to customers)' : ''}
        </div>
      )}
      {lightboxOpen && (
        <ImageLightbox
          images={activeImages}
          index={activeImg}
          onIndexChange={setActiveImg}
          onClose={() => setLightboxOpen(false)}
          alt={product.name}
        />
      )}
      {/* Breadcrumb */}
      <section className="pt-36 md:pt-48 pb-5 md:pb-6 border-b border-neutral-100">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10">
          <div className="flex items-center gap-2 md:gap-3 text-xs md:text-sm text-neutral-500 whitespace-nowrap overflow-hidden">
            <Link to="/" className="hover:text-[#FF6B00] shrink-0">Home</Link>
            <span className="shrink-0">/</span>
            {collection && (
              <>
                <Link to={`/collections/${collection.id}`} className="hover:text-[#FF6B00] shrink-0 truncate max-w-[40%]">{collection.name}</Link>
                <span className="shrink-0">/</span>
              </>
            )}
            <span className="text-neutral-900 truncate min-w-0">{product.name}</span>
          </div>
        </div>
      </section>

      {/* Product Main */}
      <section className="py-8 md:py-12">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-8 lg:gap-14">
          {/* Images */}
          <div>
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              aria-label="Zoom image"
              data-testid="product-main-image"
              className="group relative w-full aspect-square bg-white border border-neutral-200 overflow-hidden flex items-center justify-center cursor-zoom-in"
            >
              <img src={activeImages[activeImg] || activeImages[0]} alt={product.name} className="w-full h-full object-contain" />
              <span className="absolute bottom-3 right-3 bg-black/70 text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <ZoomIn size={18} />
              </span>
            </button>
            <div className="grid grid-cols-4 gap-2 sm:gap-3 mt-3 sm:mt-4">
              {activeImages.map((img, idx) => (
                <button
                  key={img}
                  onClick={() => setActiveImg(idx)}
                  className={`aspect-square overflow-hidden border-2 transition-colors ${activeImg === idx ? 'border-[#FF6B00]' : 'border-transparent hover:border-neutral-300'}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Details */}
          <div>
            <div className="font-display tracking-[0.22em] text-[11px] md:text-xs text-[#FF6B00] font-semibold mb-3">{product.brand.toUpperCase()} &middot; {product.category.toUpperCase()}</div>
            <h1 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.1] break-words">{product.name}</h1>
            {getBrandForName(product.brand) && (
              <Link
                to={`/brands/${getBrandForName(product.brand).slug}`}
                className="inline-flex items-center gap-1 mt-3 text-sm text-neutral-500 hover:text-[#FF6B00] transition-colors font-display tracking-wide"
              >
                Shop all {product.brand} &rarr;
              </Link>
            )}
            <div className="mt-4 md:mt-5 flex items-baseline gap-3 flex-wrap">
              <div className="text-2xl md:text-3xl font-display">${displayPrice.toLocaleString()} <span className="text-sm text-neutral-500 align-middle">USD</span></div>
              {onSale && (
                <>
                  <div className="text-lg font-display text-neutral-400 line-through">${displayCompare.toLocaleString()}</div>
                  <span data-testid="product-sale-badge" className="bg-[#FF6B00] text-white text-[10px] font-display tracking-[0.2em] px-2 py-1">SALE</span>
                </>
              )}
            </div>

            {showVariantPicker && (
              <div className="mt-6" data-testid="product-variants">
                <div className="font-display tracking-[0.2em] text-[10px] text-neutral-500 mb-2">{(product.optionName || 'OPTION').toUpperCase()}</div>
                <div className="flex flex-wrap gap-2">
                  {product.variants.map((v, idx) => {
                    const isLocked = !!v.locked;
                    const isActive = idx === variantIdx;
                    return (
                      <button
                        key={`${v.sku}-${idx}`}
                        data-testid={`variant-option-${idx}`}
                        type="button"
                        disabled={isLocked}
                        aria-pressed={isActive}
                        onClick={() => {
                          if (isLocked) return;
                          setVariantIdx(idx);
                          setSelectionMade(true);
                        }}
                        className={`px-4 py-2 border text-sm font-display tracking-wide transition-colors ${
                          isActive
                            ? 'border-[#FF6B00] bg-[#FF6B00] text-white'
                            : 'border-neutral-300 text-neutral-800 hover:border-neutral-500'
                        } ${isLocked ? 'cursor-not-allowed opacity-100' : ''}`}
                      >
                        {v.option || `Option ${idx + 1}`}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {hasDiameters && (
              <div className="mt-6" data-testid="product-diameters">
                <div className="font-display tracking-[0.2em] text-[10px] text-neutral-500 mb-2">DIAMETER</div>
                <div className="flex flex-wrap gap-2">
                  {activeDiameters.map((d) => (
                    <button
                      key={d.size}
                      type="button"
                      data-testid={`diameter-option-${d.size.replace(/"/g, 'in')}`}
                      onClick={() => { setDiameter(d.size); setSelectionMade(true); }}
                      className={`px-3 sm:px-4 py-2 border text-sm font-display tracking-wide transition-colors flex flex-col items-center min-w-[56px] sm:min-w-[64px] ${
                        d.size === diameter
                          ? 'border-[#FF6B00] bg-[#FF6B00] text-white'
                          : 'border-neutral-300 text-neutral-800 hover:border-neutral-500'
                      }`}
                    >
                      <span>{d.size}</span>
                      {d.price != null && (
                        <span className={`text-[10px] mt-0.5 ${d.size === diameter ? 'text-white/85' : 'text-neutral-500'}`}>
                          ${d.price.toLocaleString()}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-neutral-500 mt-2 leading-relaxed">
                  Custom width, offset, finish, bolt pattern and center bore are configured after order &mdash; our team will reach out for spec confirmation.
                </p>
              </div>
            )}

            {/* CTAs (buy actions + payment disclaimer) surfaced directly under
                the price / option-material selection on ALL screen sizes, above
                the long-form description. Both variants render but only the one
                matching the current breakpoint is visible. */}
            <div className="mt-6">
              {renderProductCTAs('mobile')}
              {renderProductCTAs('desktop')}
            </div>

            <div className="h-[1px] w-full bg-neutral-200 my-7" />

            {product.descriptionHtml ? (
              <div
                className="text-neutral-700 leading-relaxed mb-6 product-html-description"
                dangerouslySetInnerHTML={{ __html: product.descriptionHtml.replace(/&nbsp;|\u00a0/g, ' ') }}
              />
            ) : (
              <p className="text-neutral-700 leading-relaxed mb-6 whitespace-pre-line">{product.description}</p>
            )}

            <div className="space-y-3 mb-8">
              {(product.features || []).map((f) => (
                <div key={f} className="flex items-center gap-3 text-sm">
                  <Check size={18} className="text-[#FF6B00]" />
                  <span>{f}</span>
                </div>
              ))}
            </div>

            {/* CTA stack relocated above the description (rendered under the
                price/option selection for both mobile and desktop). */}

            {/* The legacy "Request Quote" mailto form was removed 2026-06-15
                — it duplicated the Reserve / Add-to-cart CTAs now that the
                checkout flow handles inquiries via /checkout. Customers who
                want a personal quote can still use /contact. */}

            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="flex items-start gap-2">
                <ShieldCheck className="text-[#FF6B00] mt-0.5" size={20} />
                <div className="text-xs"><div className="font-semibold">Quality Assured</div><div className="text-neutral-500">OEM-grade fitment</div></div>
              </div>
              <div className="flex items-start gap-2">
                <Truck className="text-[#FF6B00] mt-0.5" size={20} />
                <div className="text-xs"><div className="font-semibold">Worldwide Ship</div><div className="text-neutral-500">Crate shipping</div></div>
              </div>
              <div className="flex items-start gap-2">
                <Wrench className="text-[#FF6B00] mt-0.5" size={20} />
                <div className="text-xs"><div className="font-semibold">Install Support</div><div className="text-neutral-500">Guides included</div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Specs */}
      <section className="py-16 bg-neutral-50 border-y border-neutral-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <h2 className="font-display text-3xl tracking-tight mb-6">SPECIFICATIONS</h2>
            <div className="h-[2px] w-14 bg-[#FF6B00] mb-6" />
            <dl className="divide-y divide-neutral-200 border-t border-b border-neutral-200">
              {Object.entries(product.specs || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between py-4">
                  <dt className="font-display tracking-[0.2em] text-xs text-neutral-500">{k.toUpperCase()}</dt>
                  <dd className="text-sm text-neutral-900 text-right">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div>
            <h2 className="font-display text-3xl tracking-tight mb-6">THE FORTUNE STANDARD</h2>
            <div className="h-[2px] w-14 bg-[#FF6B00] mb-6" />
            <p className="text-neutral-700 leading-relaxed mb-4">
              Every part leaving our shop is test-fit, quality checked and finished by hand. We only ship what we would bolt onto our own cars.
            </p>
            <p className="text-neutral-700 leading-relaxed">
              Need a custom finish, exposed weave, or colored weave? Reach out &mdash; almost every spec is tuneable.
            </p>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-16">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <h2 className="font-display text-2xl md:text-3xl tracking-tight mb-8">YOU MAY ALSO LIKE</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {related.map((p) => (
                <Link key={p.id} to={`/product/${p.slug}`} className="group block">
                  <div className="aspect-square bg-neutral-100 overflow-hidden mb-4">
                    <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="font-display tracking-[0.2em] text-[10px] text-[#FF6B00] font-semibold mb-1">{p.brand.toUpperCase()}</div>
                  <h3 className="font-display text-lg tracking-wide group-hover:text-[#FF6B00] transition-colors">{p.name}</h3>
                  <div className="text-neutral-500 text-sm mt-1">${p.price.toLocaleString()} USD</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-10 border-t border-neutral-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Link to={collection ? `/collections/${collection.id}` : '/shop'} className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-[#FF6B00]">
            <ArrowLeft size={18} /> Back to {collection ? collection.name : 'Shop'}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
